import {
  FC,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
} from 'react';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { setContext } from '@apollo/client/link/context';
import { Sentry } from 'lib/sentry';
import { SeverityLevel } from '@sentry/types';
import { sha256 } from 'crypto-hash';
import {
  useAccessToken,
  generateGraphQLHeaders,
  ClientHeadersByApp,
} from '@robinpowered/dashboard-apps-common';
import fetch from 'cross-fetch';
import { v4 as uuidv4 } from 'uuid';
import { config } from '../config';

type ApolloContextValue = {
  tenantId: string | undefined;
  setTenantId: Dispatch<SetStateAction<string | undefined>>;
};

const createHttpLinkWithUri = (uri: string) =>
  createHttpLink({ uri, fetch, credentials: 'include' });

let persistedQueryLink: ApolloLink | undefined;

try {
  // IE11 & Edge will not be able to use crypto, so only add persisted queries
  // where it's available.
  if (
    typeof window?.crypto?.subtle?.digest !== 'undefined' &&
    typeof window?.TextEncoder !== 'undefined'
  ) {
    /**
     * Persisted query link allows us to reduce the overhead
     * of repeated requests by sharing a request hash with the server.
     * This link also improves compatibility with CDN access by allowing
     * graphql over GET requests
     */
    persistedQueryLink = createPersistedQueryLink({
      sha256,
      useGETForHashedQueries: true,
      disable: (error) => {
        if (error.networkError) {
          return true;
        }
        return false;
      },
    });
  }
} catch (e) {} // eslint-disable-line no-empty

const datadogLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-datadog-trace-id': uuidv4(),
    },
  }));

  return forward(operation);
});

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, response }) => {
    const captureException = (
      message: string,
      level: SeverityLevel,
      extra?: Record<string, unknown>
    ) => {
      Sentry.withScope((scope) => {
        scope.setLevel(level);
        Sentry.captureException(message, {
          tags: {
            graphql: true,
          },
          extra: {
            ...extra,
            operation,
            response,
          },
        });
      });
    };

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        captureException(`[GraphQL error]: Message: ${message}`, 'error', {
          path,
          locations,
        });
      });
    }
    if (networkError) {
      captureException(`[Network error]: ${networkError}`, 'info');
    }
  }
);

const createApolloClient = (
  accessToken: string | null,
  tenantId: string | undefined,
  gqlUrl: string,
  appName: string,
  appVersion: string
) => {
  const authLink = setContext((_, { headers }) => ({
    headers: generateGraphQLHeaders(
      accessToken,
      tenantId,
      ClientHeadersByApp.SHELL
    ),
  }));

  const httpLink = createHttpLinkWithUri(gqlUrl);

  return new ApolloClient({
    name: appName,
    version: appVersion,
    link: from([
      errorLink,
      authLink,
      datadogLink,
      ...(persistedQueryLink ? [persistedQueryLink] : []),
      httpLink,
    ]),
    cache: new InMemoryCache(),
    credentials: 'include',
    resolvers: {},
  });
};

const ApolloContext = createContext<ApolloContextValue>({
  tenantId: undefined,
  setTenantId: () => null,
});

export const ApolloContextProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { gqlUrl, appName, appVersion, sessionCookieName } = config;
  const [tenantId, setTenantId] = useState<string | undefined>();
  const accessToken = useAccessToken(sessionCookieName);

  const apolloClient = useMemo(
    () =>
      createApolloClient(
        accessToken?.accessToken,
        tenantId,
        gqlUrl,
        appName,
        appVersion
      ),
    [accessToken, tenantId, gqlUrl, appName, appVersion]
  );

  return (
    <ApolloContext.Provider value={{ tenantId, setTenantId }}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </ApolloContext.Provider>
  );
};

export const useApolloContext = (): ApolloContextValue => {
  return useContext(ApolloContext);
};
